import { useMemo, useState } from 'react';
import type { FC } from 'react';
import styled, { css } from 'styled-components';
import type { ThisIsForYouProps } from './types';
import { ApiService, baseMediaEndpoint } from 'shared/helpers/api';
import useWebsiteEditor from 'shared/hooks/useWebsiteEditor';
import { filterBackgroundColor } from 'shared/helpers/website';
import { generateAccessKey, sanitizeRichText } from 'shared/helpers/utils';
import useIsEmpty from 'shared/hooks/useIsEmpty';
import { ConditionalImage } from '../ConditionalImage';
import useSales from 'shared/hooks/useSales';
import { Wrapper } from '../Common';
import { FlexBox } from 'shared/components/Common';

const CardsInner = styled.div`
  padding: 80px 0 88px;
  display: flex;
  justify-content: space-between;

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    padding: 70px 0 78px;
    flex-direction: column;
    gap: 70px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
    flex-direction: column;
  `}
`;

const imageStyles = css`
  border: none;
  // position: absolute;
  overflow: hidden;
`;

const HoverableDropzoneStyled = styled(ConditionalImage)<{ width: number; height: number }>`
  max-width: 380px;
  max-height: 620px;
  width: ${({ width }) => (width ? `${width}px !important` : '386px !important')};
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #f5f5f5;
  border-radius: 5px;
  overflow: hidden;
  height: ${({ height }) => (height ? `${height}px !important` : '310px !important')};
  aspect-ratio: ${({ width, height }) => (width && height ? `${width} / ${height}` : '386 / 310')};

  & img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center center;
  }

  @media screen and (max-width: 991px) {
    width: 100% !important;
    height: auto !important;
  }

  @media screen and (max-width: 700px) {
    max-width: 100%;
    max-height: 100%;
    width: 100% !important;
  }
`;

const ImageInner = styled.div`
  position: relative;
  margin-bottom: 36px;
  width: 100%;
  display: flex;
  justify-content: center;
`;

const CardImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center center;
`;

const CardItem = styled.div<{ editable?: boolean }>`
  position: relative;
  width: 30%;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 100%;
  word-wrap: break-word;
  word-break: break-word;

  &:nth-child(1) {
    z-index: 5;
  }

  &:nth-child(2) {
    z-index: 4;
  }

  &:nth-child(3) {
    z-index: 3;
  }

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    width: 100%;
  `}
`;

const CardItemContent = styled.p<{ isEmpty?: boolean; editable?: boolean }>`
  font-size: ${({ theme: { $paragraphFont } }) => $paragraphFont};
  width: 100%;
  text-align: center;
  position: relative;

  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    &:not(:focus):before {
      content: attr(data-placeholder);
      text-align: center;
      justify-content: center;
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      align-items: center;
      width: 100%;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}
`;

export const V3: FC<ThisIsForYouProps> = (props) => {
  const { benefits = [], textColor, meta, hidden, customBackgroundColor, order = 0 } = props;

  const [isLoading, setIsLoading] = useState([false, false, false]);

  const {
    updateActiveElement,
    resetActiveElement,
    containerClassname,
    editable,
    websiteAssembler
  } = useWebsiteEditor();
  const { salesPageId, updateImageMeta, isDashboard } = useSales();

  const { checkBenefits: checkContentBenefits } = useIsEmpty(meta, 'content', true);

  // Accessibility improvements
  const skipLinkId = useMemo(() => `skip-to-cards-${meta?.['accessorKey'] || 'section'}`, [meta]);
  const sectionId = useMemo(() => `cards-section-${meta?.['accessorKey'] || 'gallery'}`, [meta]);

  const handleEditError = (error: Error | unknown) => {
    // Announce error to screen readers
    const errorAnnouncement = document.createElement('div');
    errorAnnouncement.setAttribute('aria-live', 'polite');
    errorAnnouncement.setAttribute('aria-atomic', 'true');
    errorAnnouncement.className = 'sr-only';
    errorAnnouncement.textContent = `Card edit error: ${error instanceof Error ? error.message : 'Edit action failed'}`;
    document.body.appendChild(errorAnnouncement);
    setTimeout(() => document.body.removeChild(errorAnnouncement), 1000);
  };

  const handleUploadError = (error: Error | unknown) => {
    // Announce upload error to screen readers
    const errorAnnouncement = document.createElement('div');
    errorAnnouncement.setAttribute('aria-live', 'polite');
    errorAnnouncement.setAttribute('aria-atomic', 'true');
    errorAnnouncement.className = 'sr-only';
    errorAnnouncement.textContent = `Image upload error: ${error instanceof Error ? error.message : 'Upload failed'}`;
    document.body.appendChild(errorAnnouncement);
    setTimeout(() => document.body.removeChild(errorAnnouncement), 1000);
  };

  const handleUpload = async (file: File, index: number) => {
    try {
      setIsLoading((prev) => prev.map((_, i) => (i === index - 1 ? true : false)));
      if (!meta['accessorKey'] || !editable) return;
      const formData = new FormData();
      formData.append('type', meta['accessorKey']);
      formData.append('file', file);
      const response = await ApiService.post(`/sales/sales-page/${salesPageId}/media/`, formData);
      updateImageMeta(meta['accessorKey'], response.data.file, `image_${index}`);
      setIsLoading([false, false, false]);

      // Announce successful upload
      const successAnnouncement = document.createElement('div');
      successAnnouncement.setAttribute('aria-live', 'polite');
      successAnnouncement.setAttribute('aria-atomic', 'true');
      successAnnouncement.className = 'sr-only';
      successAnnouncement.textContent = `Image ${index} uploaded successfully`;
      document.body.appendChild(successAnnouncement);
      setTimeout(() => document.body.removeChild(successAnnouncement), 1000);
    } catch (error) {
      handleUploadError(error);
      setIsLoading([false, false, false]);
    }
  };

  const images = useMemo(() => {
    const imageItems = [
      '/img/event_for_three_1.jpg',
      '/img/event_for_three_1.jpg',
      '/img/event_for_three_1.jpg'
    ];
    if (meta['image_1']) {
      if (baseMediaEndpoint?.includes('localhost')) return `${baseMediaEndpoint}${meta['image']}`;
      imageItems[0] = meta['image_1'];
    }
    if (meta['image_2']) {
      if (baseMediaEndpoint?.includes('localhost')) return `${baseMediaEndpoint}${meta['image']}`;
      imageItems[1] = meta['image_2'];
    }
    if (meta['image_3']) {
      if (baseMediaEndpoint?.includes('localhost')) return `${baseMediaEndpoint}${meta['image']}`;
      imageItems[2] = meta['image_3'];
    }
    return imageItems;
  }, [meta]);

  const [resize, setResize] = useState({
    width: meta?.['width'] ?? 0,
    height: meta?.['height'] ?? 0
  });

  // Check if all benefits are empty
  const allBenefitsEmpty = useMemo(() => {
    return benefits.every((_, index) => checkContentBenefits(index));
  }, [benefits, checkContentBenefits]);

  return (
    <FlexBox
      className={containerClassname}
      style={{
        display: hidden && !websiteAssembler ? 'none' : 'block',
        opacity: hidden ? 0.3 : 1,
        backgroundColor: customBackgroundColor || '#fff'
      }}
      role="region"
      aria-label="Image cards section"
      aria-hidden={hidden && !websiteAssembler}>
      {/* Skip link for keyboard navigation */}
      <span className="sr-only">
        <a href={`#${skipLinkId}`}>Skip to image cards section</a>
      </span>

      <Wrapper id={skipLinkId}>
        <CardsInner id={sectionId} role="list" aria-label={`Gallery with ${benefits.length} cards`}>
          {benefits.map(({ content: benefitContent }, index) => {
            const isContentEmpty = checkContentBenefits(index);
            const cardAccessKey = generateAccessKey('cic', order * 10 + index);
            const imageId = `card-image-${meta?.['accessorKey']}-${index + 1}`;
            const contentId =
              benefitContent?.id || `card-content-${meta?.['accessorKey']}-${index + 1}`;

            return (
              <CardItem
                key={index}
                editable={isDashboard}
                role="listitem"
                aria-label={`Card ${index + 1} of ${benefits.length}`}
                aria-setsize={benefits.length}
                aria-posinset={index + 1}>
                <ImageInner role="group" aria-labelledby={imageId}>
                  {/* Loading announcement for screen readers */}
                  {isLoading[index] && (
                    <div className="sr-only" aria-live="assertive" aria-atomic="true">
                      Uploading image {index + 1}...
                    </div>
                  )}

                  <HoverableDropzoneStyled
                    width={resize.width || meta['width']}
                    height={resize.height || meta['height']}
                    isSalePage
                    css={imageStyles as never}
                    disabled={!editable}
                    onUpload={(file) => handleUpload(file, index + 1)}
                    loading={isLoading[index]}
                    alreadyUploaded={Boolean(images[index])}
                    imageFile={images[index]}
                    size="md"
                    enableLinks
                    initialPosition={{
                      x: meta[`x_${index + 1}`] || 0,
                      y: meta[`y_${index + 1}`] || 0,
                      width: meta[`d_width_${index + 1}`],
                      height: meta[`d_height_${index + 1}`]
                    }}
                    meta={meta}
                    positionEnabled
                    handlePosition={(width: number, height: number, x: number, y: number) => {
                      updateImageMeta(meta['accessorKey'], x, `x_${index + 1}`);
                      updateImageMeta(meta['accessorKey'], y, `y_${index + 1}`);
                      updateImageMeta(meta['accessorKey'], width, `d_width_${index + 1}`);
                      updateImageMeta(meta['accessorKey'], height, `d_height_${index + 1}`);
                    }}
                    resizeSelector={editable}
                    handleResize={({ width, height }) => {
                      setResize({ width, height });
                      updateImageMeta(meta['accessorKey'], width, 'width');
                      updateImageMeta(meta['accessorKey'], height, 'height');
                    }}
                    imageAspectRatio={{
                      ratio: '5x4',
                      pixels:
                        resize.width && resize.height
                          ? `${resize.width}x${resize.height}`
                          : '760x620'
                    }}
                    aria-label={`Upload zone for card ${index + 1} image`}
                    aria-describedby={contentId}>
                    <CardImg
                      id={imageId}
                      src={images[index]}
                      alt={`Card ${index + 1} image${!isContentEmpty && benefitContent?.val ? `: ${benefitContent.val.replace(/<[^>]*>/g, '').substring(0, 50)}` : ''}`}
                      width={resize.width || 500}
                      height={resize.height || 500}
                    />
                  </HoverableDropzoneStyled>
                </ImageInner>
                <FlexBox
                  flexDirection="column"
                  justifyContent="center"
                  alignItems="center"
                  width="100%"
                  role="group"
                  aria-labelledby={contentId}>
                  <CardItemContent
                    id={contentId}
                    dangerouslySetInnerHTML={{
                      __html: sanitizeRichText(benefitContent?.val, editable)
                    }}
                    style={{
                      color: filterBackgroundColor(customBackgroundColor || '#fff', textColor)
                    }}
                    isEmpty={isContentEmpty}
                    {...(isDashboard
                      ? {
                          onClick: (e: React.MouseEvent<HTMLElement>) => {
                            try {
                              updateActiveElement(e);
                            } catch (error) {
                              handleEditError(error);
                            }
                          },
                          onBlur: resetActiveElement,
                          'data-accessor-parent': meta['accessorKey'],
                          'data-accessorkey': 'content',
                          'data-benefits': true,
                          'data-placeholder': '+',
                          editable: true
                        }
                      : {
                          'aria-label':
                            isContentEmpty || !benefitContent?.val?.trim()
                              ? `Card ${index + 1} description`
                              : undefined,
                          'aria-describedby': imageId,
                          accessKey: cardAccessKey,
                          title: `Press Alt+${cardAccessKey} to focus card ${index + 1} description`,
                          tabIndex: 0,
                          role: 'textbox',
                          'aria-multiline': 'true'
                        })}
                  />
                </FlexBox>
              </CardItem>
            );
          })}
        </CardsInner>

        {/* Screen reader announcement for empty state */}
        {allBenefitsEmpty && !websiteAssembler && (
          <div className="sr-only" role="status" aria-live="polite">
            No cards have been added to this gallery yet.
          </div>
        )}
      </Wrapper>
    </FlexBox>
  );
};

V3.displayName = 'ThisIsForYouV3';
